import type { SelectOption } from "../components/Select.js";

/** A flat parent/child row — what a list endpoint returns before ordering. */
export interface TreeNode {
  id: string;
  /** Parent row id; null/undefined (or a parent outside the list) means root. */
  parentId?: string | null;
  label: string;
  /** Extra text to match on (a slug, a code) beyond the label and ancestors. */
  searchText?: string;
  disabled?: boolean;
}

/**
 * Order a flat parent/child list into `Select` options as a tree: each child
 * directly under its parent, siblings alphabetically, `depth` set for the
 * indent and `hint` set to the parent's label. The hint matters because a
 * search reorders rows by score — once the list is filtered the indent means
 * nothing, but the parent name still says which organization a company is
 * under. Ancestor labels also go into `searchText`, so searching a parent
 * surfaces everything beneath it.
 *
 * Nothing is ever dropped: rows in a parent cycle (bad data) can't be reached
 * from a root, so they are appended flat at the end rather than disappearing.
 */
export function treeSelectOptions(nodes: readonly TreeNode[], locale?: string): SelectOption[] {
  const present = new Set(nodes.map((n) => n.id));
  const roots: TreeNode[] = [];
  const childrenOf = new Map<string, TreeNode[]>();
  for (const node of nodes) {
    const parentId = node.parentId != null && present.has(node.parentId) ? node.parentId : null;
    if (parentId === null) {
      roots.push(node);
      continue;
    }
    const siblings = childrenOf.get(parentId);
    if (siblings) siblings.push(node);
    else childrenOf.set(parentId, [node]);
  }

  const collator = new Intl.Collator(locale, { sensitivity: "base", numeric: true });
  const byLabel = (a: TreeNode, b: TreeNode): number => collator.compare(a.label, b.label);

  const out: SelectOption[] = [];
  const emitted = new Set<string>();
  const walk = (level: readonly TreeNode[], depth: number, trail: readonly string[]): void => {
    for (const node of [...level].sort(byLabel)) {
      if (emitted.has(node.id)) continue;
      emitted.add(node.id);
      const parent = trail[trail.length - 1];
      out.push({
        value: node.id,
        label: node.label,
        ...(depth > 0 ? { depth } : {}),
        ...(parent !== undefined ? { hint: parent } : {}),
        ...(node.disabled !== undefined ? { disabled: node.disabled } : {}),
        searchText: [node.label, node.searchText, ...trail].filter(Boolean).join(" "),
      });
      walk(childrenOf.get(node.id) ?? [], depth + 1, [...trail, node.label]);
    }
  };
  walk(roots, 0, []);

  const orphans = nodes.filter((n) => !emitted.has(n.id)).sort(byLabel);
  for (const node of orphans) {
    out.push({
      value: node.id,
      label: node.label,
      ...(node.disabled !== undefined ? { disabled: node.disabled } : {}),
      searchText: [node.label, node.searchText].filter(Boolean).join(" "),
    });
  }
  return out;
}
